import { Model } from '@vuex-orm/core';
import moment from 'moment';
import { Call } from './Call';

/**
 * CallAttempt Status
 *
 * https://www.twilio.com/docs/voice/make-calls#get-call-status-events-during-a-call
 */
export const STATUS = {
  VOLUNTEER_QUEUED: 'volunteer-queued', // Staat in de wachtrij bij Twilio
  VOLUNTEER_INITIATED: 'volunteer-initiated', // Twilio begint met bellen naar vrijwilliger
  VOLUNTEER_RINGING: 'volunteer-ringing', // Telefoon van vrijwilliger gaat over
  VOLUNTEER_IN_PROGRESS: 'volunteer-in-progress', // vrijwilliger heeft telefoon opgenomen
  VOLUNTEER_COMPLETED: 'volunteer-completed', // Gesprek is afgelopen nadat vrijwilliger heeft geantwoord
  VOLUNTEER_BUSY: 'volunteer-busy', // Vrijwilliger is in gesprek
  VOLUNTEER_CANCELED: 'volunteer-canceled',
  VOLUNTEER_FAILED: 'volunteer-failed', // ???
  VOLUNTEER_NO_ANSWER: 'volunteer-no-answer', // Vrijwilliger heeft niet opgenomen
  CLIENT_COMPLETED: 'client-completed', // Gesprek is afgelopen nadat client heeft geantwoord
  CLIENT_BUSY: 'client-busy', // Client is in gesprek
  CLIENT_CANCELED: 'client-canceled',
  CLIENT_FAILED: 'client-failed', // Client heeft niet opgenomen na x seconden
  CLIENT_NO_ANSWER: 'client-no-answer' // Client heeft niet opgenomen
};
export const STATUS_NAME = {
  [STATUS.VOLUNTEER_QUEUED]: 'In de rij',
  [STATUS.VOLUNTEER_INITIATED]: 'Geïnitieerd',
  [STATUS.VOLUNTEER_RINGING]: 'Gaat over bij vrijwilliger',
  [STATUS.VOLUNTEER_IN_PROGRESS]: 'Client wordt gebeld',
  [STATUS.VOLUNTEER_COMPLETED]: 'Vrijwilliger voltooid',
  [STATUS.VOLUNTEER_BUSY]: 'Bellen mislukt: je bent in gesprek',
  [STATUS.VOLUNTEER_CANCELED]: 'Bellen mislukt',
  [STATUS.VOLUNTEER_FAILED]: 'Bellen mislukt',
  [STATUS.VOLUNTEER_NO_ANSWER]: 'Bellen mislukt: je hebt niet opgenomen',
  [STATUS.CLIENT_COMPLETED]: 'Client voltooid',
  [STATUS.CLIENT_BUSY]: 'Bellen mislukt: client in gesprek',
  [STATUS.CLIENT_CANCELED]: 'Bellen mislukt',
  [STATUS.CLIENT_FAILED]: 'Bellen mislukt',
  [STATUS.CLIENT_NO_ANSWER]: 'Bellen mislukt: client heeft niet opgenomen'
};

/**
 * ORM Model
 * @see {@link https://vuex-orm.github.io/vuex-orm/guide/components/models.html}
 */
export class CallAttempt extends Model {
  static entity = 'callAttempts'

  static primaryKey = 'id'

  static initiatingStatusses = [
    STATUS.VOLUNTEER_QUEUED,
    STATUS.VOLUNTEER_INITIATED,
    STATUS.VOLUNTEER_RINGING
  ];

  static callingStatusses = [
    STATUS.VOLUNTEER_IN_PROGRESS
  ];

  static failedStatusses = [
    STATUS.VOLUNTEER_BUSY,
    STATUS.VOLUNTEER_CANCELED,
    STATUS.VOLUNTEER_FAILED,
    STATUS.VOLUNTEER_NO_ANSWER,
    STATUS.CLIENT_BUSY,
    STATUS.CLIENT_CANCELED,
    STATUS.CLIENT_FAILED,
    STATUS.CLIENT_NO_ANSWER
  ];

  static successStatusses = [
    STATUS.VOLUNTEER_COMPLETED,
    STATUS.CLIENT_COMPLETED
  ];

  static fields () {
    return {
      id: this.attr(null),
      duration: this.number(0),
      call_id: this.attr(null),
      start: this.attr(null),
      end: this.attr(null),
      status: this.string(null),
      status_webhook_url: this.string(null),

      call: this.belongsTo(Call, 'call_id')
    };
  }

  get is_initiating () {
    return CallAttempt.initiatingStatusses.includes(this.status);
  }

  get is_calling () {
    return CallAttempt.callingStatusses.includes(this.status);
  }

  get is_failed () {
    return CallAttempt.failedStatusses.includes(this.status);
  }

  get is_finished () {
    return CallAttempt.successStatusses.includes(this.status);
  }

  get is_active () {
    return this.is_initiating || this.is_calling;
  }

  get status_color () {
    if (this.is_failed) {
      return 'has-text-danger';
    }
    return 'has-text-warning';
  }

  /**
   * Get the channel Id.
   */
  get channelId () {
    return `regicall.callAttempts.${this.id}.events`;
  }

  /**
   * Get the friendly name for the status
   */
  get status_name () {
    return STATUS_NAME[this.status];
  }

  get start_time () {
    return moment(this.start).format('H:mm');
  }

  get formattedDuration () {
    if (!this.duration) return '';
    const duration = moment.duration(this.duration, 'seconds');
    const hours = duration.hours();
    const minutes = duration.minutes();
    const seconds = duration.seconds();
    let result = '';
    if (hours > 0) {
      result += `${hours}h `;
    }
    if (minutes > 0) {
      result += `${minutes}m `;
    }
    result += `${seconds}s`;
    return result;
  }

  static afterCreate (model) {
    if (model.is_active) {
      window.Echo.channel(model.channelId)
        .listen('CallAttemptStatusUpdated', (e) => {
          model.$update({ status: e.status, duration: e.duration });
        });
    }
  }

  /**
   * Axios default overrides
   * @see {@link https://github.com/vuex-orm/plugin-axios/blob/master/README.md}
   */
  static methodConf = {
    http: {
      url: '/regicall/callAttempts'
    },
    methods: {
    }
  }
}

